/**
 * **Stream C — the poster and the decks** (Documents.dc.html): the finance-year poster (C06) and the
 * two slide decks (D07 · D08). Every other Stream C document is a drawn page of its own, laid out
 * by its engine (`aod-package.tsx` … `payee-letters.tsx`); every print goes on the print log.
 */
import type { ReactNode } from 'react';
import { financeYearPoster, type PosterStep } from '@cider/domain/engines';
import { formatMoney } from '@cider/ui';
import { useLoad, type Report } from '../views/money/shared.js';
import { usePrinted } from './printed.js';
import { SheetPages, type SheetLine } from './sheets.js';
import { DrawnPages } from './drawn-doc.js';

const fyQ = (fy: number): string => `fy=${String(fy)}`;

const cell = (
  r: Report['sections'][number],
  c: string | number | null,
  i: number,
): string | null =>
  c === null ? null : typeof c === 'number' && r.money.includes(i) ? formatMoney(c) : String(c);

/**
 * **C06 · The finance-year calendar** (11×17, Documents.dc.html): the `finance_year_poster` engine
 * lays out The finance year's own rows, drawn at half scale and printed on the poster sheet.
 * Reprinted when a Year setting moves a date. Scored against the drawing by walk 99g.
 */
export function FinanceYearPoster({ fy }: { fy: number }): ReactNode {
  const d = useLoad<{ steps: PosterStep[] }>(`/money/finance-year/${String(fy)}`);
  const ready = d.data !== null || d.error !== null;
  usePrinted('finance-year-poster', ready, { year: fy - 1 });
  if (!ready) return <p>Reading the finance year…</p>;
  return <DrawnPages pages={[financeYearPoster(fy, d.data?.steps ?? null)]} size="poster" />;
}

/** The two decks (D07 · D08): a cover, then one slide per section of the engine's reports. */
const DECKS: Record<
  string,
  {
    title: (fy: number) => string;
    order: string[];
    reports: { kind: string; q: (fy: number) => string }[];
  }
> = {
  'budget-slides': {
    title: (fy) => `${String(fy - 1)} budget · the meeting`,
    order: [
      'The baseline — every line from its rule',
      'The two sides — receipts and costs by area',
      'The Asks',
      'The vote',
      'The budget locks at season open',
    ],
    reports: [{ kind: 'budget-vs-actual', q: fyQ }],
  },
  'closeout-slides': {
    title: (fy) => `Close-out · FY${String(fy)}`,
    order: [
      'The four figures',
      'The year by account',
      'Distributions to confirm',
      'Projects to vote',
    ],
    reports: [
      { kind: 'one-pager', q: fyQ },
      { kind: 'pl-year', q: fyQ },
    ],
  },
};

/** **The slide decks** (Stream D, D07 · D08) at 16:9, Print's slide family — figures from the engine. */
export function SlideDeck({ deck, fy }: { deck: string; fy: number }): ReactNode {
  const d = DECKS[deck];
  const r0 = useLoad<Report>(
    d?.reports[0] ? `/money/reports/${d.reports[0].kind}?${d.reports[0].q(fy)}` : null,
  );
  const r1 = useLoad<Report>(
    d?.reports[1] ? `/money/reports/${d.reports[1].kind}?${d.reports[1].q(fy)}` : null,
  );
  const loads = [r0, r1].slice(0, d?.reports.length ?? 0);
  const ready = loads.every((l) => l.data !== null || l.error !== null);
  usePrinted(`slides-${deck}`, !!d && ready, { year: fy - 1 });
  if (!d) return <p role="alert">No such deck.</p>;
  if (!ready) return <p>Reading the figures…</p>;
  const sections = loads.flatMap((l) => {
    const rep = l.data && 'sections' in l.data ? l.data : null;
    if (!rep || rep.status === 'NO DATA')
      return [
        {
          head: rep?.title ?? 'NO DATA',
          lines: [{ cells: [`NO DATA — ${rep?.says ?? 'the report did not answer'}`] }],
        },
      ];
    return rep.sections.map((s) => ({
      head: s.head,
      lines: [
        ...s.rows.map((row, i) => ({
          key: String(i),
          cells: row.map((c, j) => cell(s, c, j) ?? ''),
        })),
        ...(s.totals ? [{ cells: s.totals.map((c, j) => cell(s, c, j) ?? '') }] : []),
      ] as SheetLine[],
    }));
  });
  const name = d.title(fy);
  return (
    <>
      <SheetPages
        doc={{
          size: 'slide',
          title: name,
          sub: 'Tonight',
          name: `${name} · slide 1`,
          lines: d.order.map((o, i) => ({ key: o, cells: [`${String(i + 1)} · ${o}`] })),
        }}
      />
      {sections.map((s, i) => (
        <SheetPages
          key={`${s.head}${String(i)}`}
          doc={{
            size: 'slide',
            title: s.head,
            name: `${name} · slide ${String(i + 2)}`,
            cols: ['wide', 'fig', 'fig', 'fig', 'fig'],
            lines: s.lines,
          }}
        />
      ))}
    </>
  );
}
